import { useState } from "react";

import {
  errorMessage,
  useCreateEnvironment,
  useDeleteEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from "../api/queries";
import type { EnvironmentOption } from "../api/types";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";

interface FormValues {
  name: string;
  description: string;
  reference: string;
  shared: boolean;
}

const EMPTY_FORM: FormValues = {
  name: "",
  description: "",
  reference: "",
  shared: false,
};

/** One line saying where a saved environment came from and who sees it. */
function provenance(option: EnvironmentOption): string {
  const owner = option.added_by ?? "";
  return option.shared
    ? `added by ${owner} — visible to the whole team`
    : `added by ${owner} — only you see it`;
}

interface SavedRowProps {
  option: EnvironmentOption;
  onEdit: (option: EnvironmentOption) => void;
  onDelete: (id: string) => void;
  deleting: boolean;
}

function SavedRow({
  option,
  onEdit,
  onDelete,
  deleting,
}: SavedRowProps): JSX.Element {
  return (
    <li className="env-row">
      <div className="env-row__text">
        <span className="env-row__name">{option.name}</span>
        {option.description ? (
          <span className="env-row__description">{option.description}</span>
        ) : null}
        <span className="env-row__reference mono">{option.reference}</span>
        <span className="env-row__provenance">{provenance(option)}</span>
      </div>
      {option.editable ? (
        <div className="env-row__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => onEdit(option)}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={deleting}
            onClick={() => onDelete(option.id)}
          >
            Delete
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Environments: what a workspace runs, including the ones you add yourself.
 *
 * Returns:
 *     The environments screen — the built-in list, everything the team saved,
 *     and the form that saves a new one.
 */
export function EnvironmentsPage(): JSX.Element {
  const environments = useEnvironments();
  const create = useCreateEnvironment();
  const update = useUpdateEnvironment();
  const remove = useDeleteEnvironment();

  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  /** Id of the saved environment being edited; null means creating. */
  const [editing, setEditing] = useState<string | null>(null);

  const header = (
    <PageHeader
      eyebrow="Environments"
      title="Environments"
      subtitle="What your workspaces and jobs run. Add your own and name it."
    />
  );

  if (isClusterProblem(environments.error)) {
    return (
      <>
        {header}
        <ClusterProblem
          error={environments.error}
          onRetry={() => void environments.refetch()}
        />
      </>
    );
  }
  if (environments.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the environments…" />
      </>
    );
  }
  if (environments.isError) {
    return (
      <>
        {header}
        <ErrorBlock
          error={environments.error}
          onRetry={() => void environments.refetch()}
        />
      </>
    );
  }

  const items = environments.data.items;
  const builtIn = items.filter((item) => item.source === "built-in");
  const saved = items.filter((item) => item.source === "saved");
  const mutation = editing ? update : create;

  const startEditing = (option: EnvironmentOption): void => {
    setEditing(option.id);
    setValues({
      name: option.name,
      description: option.description ?? "",
      reference: option.reference,
      shared: option.shared,
    });
  };

  const resetForm = (): void => {
    setEditing(null);
    setValues(EMPTY_FORM);
    create.reset();
    update.reset();
  };

  const submit = (): void => {
    const payload = {
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      reference: values.reference.trim(),
      shared: values.shared,
    };
    if (editing) {
      update.mutate({ id: editing, ...payload }, { onSuccess: resetForm });
    } else {
      create.mutate(payload, { onSuccess: resetForm });
    }
  };

  return (
    <>
      {header}

      <section className="section" aria-labelledby="saved-envs-title">
        <h2 className="section__title" id="saved-envs-title">
          Added by the team
        </h2>
        {saved.length === 0 ? (
          <p className="capacity__empty">
            Nobody has added an environment yet — the form below is how.
          </p>
        ) : (
          <ul className="env-list">
            {saved.map((option) => (
              <SavedRow
                key={option.id}
                option={option}
                onEdit={startEditing}
                onDelete={(id) => remove.mutate(id)}
                deleting={remove.isPending}
              />
            ))}
          </ul>
        )}
        {remove.isError ? (
          <p className="form__error" role="alert">
            {errorMessage(remove.error)}
          </p>
        ) : null}
      </section>

      <section className="section" aria-labelledby="env-form-title">
        <h2 className="section__title" id="env-form-title">
          {editing ? "Edit environment" : "Add an environment"}
        </h2>
        <div className="section__lead">
          <p>
            <strong>Most people never need one.</strong> Your home folder is
            kept between workspaces, so packages you install stay installed:
            run <span className="mono">uv pip install …</span> once in a
            workspace and it is still there tomorrow, and in your next
            workspace. Add an environment only when you want something the
            machine itself must carry — a different CUDA, a system library, an
            exact set-up you want to hand to somebody else.
          </p>
          <p>
            An environment is a container image, named by where it is stored.
            What you can use depends on where it runs:
          </p>
          <ul className="env-help">
            <li>
              <strong>For a job</strong> — any public image works, because a
              job just runs your command inside it. Paste something like{" "}
              <span className="mono">
                pytorch/pytorch:2.5.1-cuda12.4-cudnn9-runtime
              </span>{" "}
              and you are done: nothing to build.
            </li>
            <li>
              <strong>For a workspace</strong> — the image must also carry the
              SSH server you connect through, so it has to be built from this
              product&apos;s recipe (<span className="mono">
                backend/workspace-image/
              </span>{" "}
              in the gpu-console repository) and pushed to the team registry. A
              plain public image will start and give you no way in.
            </li>
          </ul>
        </div>
        <form
          className="job-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field
            label="Name"
            htmlFor="env-name"
            hint="What the picker shows — call it what you call it."
          >
            <input
              id="env-name"
              className="input"
              value={values.name}
              onChange={(event) =>
                setValues((current) => ({ ...current, name: event.target.value }))
              }
            />
          </Field>
          <Field
            label="Description"
            htmlFor="env-description"
            hint="One line on what is inside. Optional."
          >
            <input
              id="env-description"
              className="input"
              value={values.description}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
          </Field>
          <Field
            label="Image reference"
            htmlFor="env-reference"
            hint="registry/repository:tag — e.g. pytorch/pytorch:2.5.1-cuda12.4-cudnn9-runtime for a job, or rg.fr-par.scw.cloud/neuralk-prod/my-env:v1 for a workspace"
          >
            <input
              id="env-reference"
              className="input mono"
              spellCheck={false}
              value={values.reference}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  reference: event.target.value,
                }))
              }
            />
          </Field>
          <label className="field__label" htmlFor="env-shared">
            <input
              id="env-shared"
              type="checkbox"
              checked={values.shared}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  shared: event.target.checked,
                }))
              }
            />{" "}
            Share with the team — everybody sees it, only you can change it
          </label>

          {mutation.isError ? (
            <p className="form__error" role="alert">
              {errorMessage(mutation.error)}
            </p>
          ) : null}

          <div className="env-form__actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                mutation.isPending ||
                !values.name.trim() ||
                !values.reference.trim()
              }
            >
              {mutation.isPending
                ? "Saving…"
                : editing
                  ? "Save changes"
                  : "Save environment"}
            </button>
            {editing ? (
              <button type="button" className="btn btn--ghost" onClick={resetForm}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="section" aria-labelledby="builtin-envs-title">
        <h2 className="section__title" id="builtin-envs-title">
          Built in
        </h2>
        <ul className="env-list">
          {builtIn.map((option) => (
            <li key={option.id} className="env-row">
              <div className="env-row__text">
                <span className="env-row__name">{option.name}</span>
                {option.description ? (
                  <span className="env-row__description">
                    {option.description}
                  </span>
                ) : null}
                <span className="env-row__reference mono">
                  {option.reference}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
